/**
 * Return Policy utilities.
 *
 * Define and display per-listing return/refund policies.
 *
 * Policy stored in listing.publicData.returnPolicy = {
 *   type: 'no_returns' | 'full_refund' | 'partial_refund' | 'exchange_only',
 *   windowDays: number,         — days after purchase to return
 *   restockingFeePercent: number, — 0-100, default 0
 *   conditions: string          — free-text custom conditions
 * }
 *
 * Built-in policy types (simplified):
 *   no_returns, full_refund (100%), partial_refund (custom %), exchange_only
 */

export const POLICY_TYPES = ['no_returns', 'full_refund', 'partial_refund', 'exchange_only'];
export const DEFAULT_RETURN_WINDOW_DAYS = 30;

/**
 * Get return policy from listing.
 * @param {Object|null} listing
 * @returns {Object|null}
 */
export const getReturnPolicy = (listing) =>
  listing?.attributes?.publicData?.returnPolicy || null;

/**
 * Check if returns are allowed.
 * @param {Object|null} policy
 * @returns {boolean}
 */
export const allowsReturns = (policy) =>
  !!policy && policy.type !== 'no_returns';

/**
 * Check if return window has expired.
 * @param {Date|string} purchaseDate
 * @param {Object} policy
 * @param {Date} [now]
 * @returns {boolean}
 */
export const isReturnWindowOpen = (purchaseDate, policy, now = new Date()) => {
  if (!allowsReturns(policy)) return false;
  const days = policy.windowDays || DEFAULT_RETURN_WINDOW_DAYS;
  const deadline = new Date(purchaseDate);
  deadline.setDate(deadline.getDate() + days);
  return now <= deadline;
};

/**
 * Calculate refund amount based on policy.
 * @param {number} priceCents
 * @param {Object} policy
 * @returns {{ refundCents: number, restockingCents: number }}
 */
export const calculateRefund = (priceCents, policy) => {
  if (!allowsReturns(policy)) return { refundCents: 0, restockingCents: 0 };

  const restockingCents = Math.round(priceCents * ((policy.restockingFeePercent || 0) / 100));
  const refundCents = priceCents - restockingCents;
  return { refundCents, restockingCents };
};

/**
 * Validate return policy config.
 * @param {Object} policy
 * @returns {string|null}
 */
export const validatePolicy = (policy) => {
  if (!POLICY_TYPES.includes(policy?.type)) return 'Invalid policy type';
  if (policy.windowDays !== undefined && (policy.windowDays < 1 || policy.windowDays > 365)) {
    return 'Return window must be 1-365 days';
  }
  if (policy.restockingFeePercent !== undefined &&
    (policy.restockingFeePercent < 0 || policy.restockingFeePercent > 100)) {
    return 'Restocking fee must be 0-100%';
  }
  return null;
};
